// SkeletonLineGraph.tsx
import React, { useMemo } from 'react';
import { Skeleton } from '@mui/material';
import styles from './SkeletonLineGraph.module.css';

interface SkeletonLineGraphProps {
  /**
   * Number of skeleton graphs to render
   * @default 3
   */
  count?: number;
}

/**
 * SkeletonLineGraph Component - Displays loading skeleton for line graphs
 * Mimics the structure of the LineGraph component with header and chart area
 */
const SkeletonLineGraph: React.FC<SkeletonLineGraphProps> = ({ count = 3 }) => {
  const skeletonElements = useMemo(
    () =>
      [...Array(count)].map((_, index) => (
        <div
          key={`skeleton-graph-${index}`}
          className={styles.skeletonContainer}>
          {/* Header Section */}
          <div className={styles.header}>
            <div className={styles.leftSection}>
              {/* Title */}
              <Skeleton
                variant="text"
                width={180}
                height={24}
                sx={{ marginBottom: '8px' }}
              />
              {/* Main Value */}
              <Skeleton
                variant="text"
                width={120}
                height={36}
                sx={{ marginBottom: '8px' }}
              />
              {/* Trend Indicator */}
              <Skeleton
                variant="text"
                width={100}
                height={20}
                sx={{ marginBottom: '4px' }}
              />
              {/* Trend Label */}
              <Skeleton
                variant="text"
                width={80}
                height={16}
              />
            </div>
            <div className={styles.rightSection}>
              {/* Date Range */}
              <Skeleton
                variant="text"
                width={140}
                height={18}
                sx={{ marginBottom: '4px' }}
              />
              {/* Time Period */}
              <Skeleton
                variant="text"
                width={60}
                height={16}
              />
            </div>
          </div>

          {/* Chart Area */}
          <Skeleton
            variant="rounded"
            width="100%"
            height={250}
            sx={{ marginTop: '16px' }}
          />

          {/* Legend */}
          <div className={styles.legend}>
            <Skeleton
              variant="text"
              width={100}
              height={20}
            />
            <Skeleton
              variant="text"
              width={100}
              height={20}
            />
          </div>
        </div>
      )),
    [count],
  );

  return <>{skeletonElements}</>;
};

export default SkeletonLineGraph;
